// frontend/src/pages/SupplierNegotiationPage.js
import React, { useState, useEffect } from 'react';
import { 
    Search, Handshake, ArrowLeft, Save, Loader2, 
    User, Mail, Phone, FileText, Building2, Package, Banknote, Clock, FileEdit, AlertCircle,
    ChevronLeft, ChevronRight 
} from 'lucide-react';
import useApi from '../hooks/useApi';

const SupplierNegotiationPage = ({ currentUser }) => {
    const { apiFetch } = useApi();
    
    // --- VERIFICAÇÃO DE PERMISSÕES GRANULARES ---
    const isSuper = currentUser?.is_super_user;
    const perms = currentUser?.permissions || {};
    // Na negociação, criar e editar fazem a mesma função (salvar/atualizar a negociação do fornecedor)
    const canSave = isSuper || perms.supplier_negotiation_create || perms.supplier_negotiation_edit;

    // --- ESTADOS ---
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);

    // Paginação
    const [page, setPage] = useState(1);
    const itemsPerPage = 12;

    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [paymentConditions, setPaymentConditions] = useState([]);
    const [negotiationItems, setNegotiationItems] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Estados das Condições Comerciais Gerais do Fornecedor
    const [supplierPaymentCondition, setSupplierPaymentCondition] = useState(''); 
    const [supplierDeliveryTime, setSupplierDeliveryTime] = useState('');
    const [supplierNotes, setSupplierNotes] = useState('');

    // --- CARREGAR DADOS INICIAIS ---
    useEffect(() => {
        const fetchConditions = async () => {
            try {
                const data = await apiFetch('/payment-conditions');
                setPaymentConditions(data || []);
            } catch (error) {
                console.error("Erro ao carregar condições de pagamento:", error);
            }
        };
        fetchConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- FUNÇÕES DE BUSCA ---
    const handleSearchSubmit = async (e) => {
        if (e) e.preventDefault();
        
        setIsSearching(true);
        setHasSearched(true);
        setSelectedSupplier(null);
        setPage(1);

        try {
            const queryParam = searchQuery.trim() ? `&search=${encodeURIComponent(searchQuery)}` : '';
            const data = await apiFetch(`/general-clients?classification=Fornecedor${queryParam}`);
            setSearchResults(data || []);
        } catch (error) {
            console.error("Erro ao buscar fornecedores:", error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSelectSupplier = async (supplier) => {
        setSelectedSupplier(supplier);
        setSearchResults([]); 
        setSearchQuery(''); 
        setIsLoadingData(true);

        try {
            const data = await apiFetch(`/supplier-negotiations/${supplier.id}/products`);
            setNegotiationItems(data.items || []);
            setSupplierPaymentCondition(data.payment_condition_id || '');
            setSupplierDeliveryTime(data.delivery_time || '');
            setSupplierNotes(data.notes || '');
        } catch (error) {
            console.error("Erro ao buscar dados do fornecedor:", error);
            setNegotiationItems([]);
            setSupplierPaymentCondition('');
            setSupplierDeliveryTime('');
            setSupplierNotes('');
        } finally {
            setIsLoadingData(false);
        }
    };

    // --- MANIPULAÇÃO DA TABELA DE NEGOCIAÇÃO ---
    const handleItemChange = (index, field, value) => {
        if (!canSave) return;
        const newItems = [...negotiationItems];
        newItems[index][field] = value;
        setNegotiationItems(newItems);
    };

    const handlePriceChange = (index, inputValue) => {
        if (!canSave) return;
        let rawValue = inputValue.replace(/\D/g, '');
        if (!rawValue) rawValue = '0';
        const numericValue = parseInt(rawValue, 10) / 100;
        handleItemChange(index, 'negotiated_price', numericValue);
    };

    // --- SALVAR NEGOCIAÇÃO ---
    const handleSaveNegotiation = async () => {
        if (!canSave) return;
        setIsSaving(true);
        try {
            const payload = {
                supplier_id: selectedSupplier.id,
                payment_condition_id: supplierPaymentCondition || null,
                delivery_time: supplierDeliveryTime || null,
                notes: supplierNotes || null,
                items: negotiationItems
            };
            
            await apiFetch(`/supplier-negotiations`, {
                method: 'POST',
                body: payload
            });
            
            alert('Valores e condições salvos com sucesso!');
            
            setSelectedSupplier(null);
            setHasSearched(false);
            setSearchResults([]);
            setSearchQuery('');
            setPage(1);

        } catch (error) {
            console.error("Erro ao salvar negociação:", error);
            alert('Erro ao salvar negociação. Verifique a conexão.');
        } finally {
            setIsSaving(false);
        }
    };

    // --- LÓGICA DE PAGINAÇÃO ---
    const indexOfLastItem = page * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentSuppliers = searchResults.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(searchResults.length / itemsPerPage);

    return (
        <div className="p-6 max-w-7xl mx-auto animate-in fade-in duration-300">
            
            {/* CABEÇALHO E BARRA DE BUSCA */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                        <Handshake className="text-blue-600" size={28} /> Negociação Fornecedor
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">Busque um fornecedor para definir os valores e as condições comerciais negociadas.</p>
                </div>

                <div className="flex w-full lg:w-auto items-center gap-3">
                    <form onSubmit={handleSearchSubmit} className="flex gap-2 flex-1 lg:w-96">
                        <div className="relative flex-1">
                            <input 
                                type="text" 
                                placeholder="Buscar fornecedor..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm h-full outline-none"
                            />
                            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                        </div>
                        <button 
                            type="submit" 
                            className="bg-blue-600 hover:bg-blue-700 text-white p-2.5 rounded-lg shadow-sm transition-colors flex items-center justify-center shrink-0"
                            title="Pesquisar"
                        >
                            {isSearching ? <Loader2 size={20} className="animate-spin" /> : <Search size={20} />}
                        </button>
                    </form>
                </div>
            </div>

            {/* ÁREA DE CONTEÚDO PRINCIPAL */}
            
            {/* 1. TELA INICIAL VAZIA */}
            {!hasSearched && !selectedSupplier && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 flex flex-col items-center justify-center text-center h-[50vh]">
                    <div className="bg-blue-50 p-4 rounded-full mb-4">
                        <Building2 size={48} className="text-blue-500" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">Localizar Fornecedor</h2>
                    <p className="text-gray-500 max-w-md">
                        Digite o nome, CNPJ ou CPF na barra acima e clique na lupa para buscar o fornecedor. Deixe vazio e clique na lupa para ver todos.
                    </p>
                </div>
            )}

            {/* 2. RESULTADOS DA BUSCA (TABELA COM PAGINAÇÃO) */}
            {hasSearched && !selectedSupplier && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                        <h3 className="font-bold text-gray-700">
                            {searchQuery ? `Resultados para: "${searchQuery}"` : "Todos os Fornecedores"}
                        </h3>
                        <span className="text-sm text-gray-500">{searchResults.length} fornecedor(es) encontrado(s)</span>
                    </div>
                    
                    {isSearching ? (
                        <div className="p-12 flex justify-center"><Loader2 size={32} className="animate-spin text-blue-600" /></div>
                    ) : searchResults.length === 0 ? (
                        <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                            <AlertCircle size={32} className="text-gray-400 mb-2" />
                            <p>Nenhum fornecedor encontrado.</p>
                        </div>
                    ) : (
                        <div className="flex flex-col h-full">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-gray-100 text-gray-600 font-semibold uppercase text-xs">
                                        <tr>
                                            <th className="px-6 py-3">Documento</th>
                                            <th className="px-6 py-3">Fornecedor</th>
                                            <th className="px-6 py-3">Classificação</th>
                                            <th className="px-6 py-3 text-center">Ação</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {currentSuppliers.map((supplier) => (
                                            <tr key={supplier.id} className="hover:bg-blue-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                                    {supplier.document_type || 'DOC'}: {supplier.document_id || '-'}
                                                </td>
                                                <td className="px-6 py-4 font-bold text-gray-800">
                                                    {supplier.name}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="text-[10px] font-bold px-2 py-1 bg-gray-100 text-gray-600 rounded uppercase">
                                                        {supplier.classification}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <button 
                                                        onClick={() => handleSelectSupplier(supplier)}
                                                        className="px-4 py-1.5 bg-white border border-blue-300 rounded hover:bg-blue-50 text-blue-600 font-semibold text-xs transition-colors"
                                                    >
                                                        Selecionar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* CONTROLES DE PAGINAÇÃO */}
                            {totalPages > 1 && (
                                <div className="p-4 border-t border-gray-200 bg-white flex flex-col sm:flex-row justify-between items-center gap-4">
                                    <span className="text-sm text-gray-500">
                                        Mostrando <span className="font-medium">{indexOfFirstItem + 1}</span> a <span className="font-medium">{Math.min(indexOfLastItem, searchResults.length)}</span> de <span className="font-medium">{searchResults.length}</span> fornecedores
                                    </span>
                                    <div className="flex gap-2 items-center">
                                        <button
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            disabled={page === 1}
                                            className="p-1.5 rounded border border-gray-300 text-gray-600 disabled:opacity-50 hover:bg-gray-50 transition-colors"
                                        >
                                            <ChevronLeft size={18} />
                                        </button>
                                        <span className="text-sm font-medium px-3 py-1 bg-gray-100 rounded text-gray-700">
                                            Página {page} de {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                            disabled={page === totalPages}
                                            className="p-1.5 rounded border border-gray-300 text-gray-600 disabled:opacity-50 hover:bg-gray-50 transition-colors"
                                        >
                                            <ChevronRight size={18} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* 3. TELA DE NEGOCIAÇÃO (FORNECEDOR SELECIONADO) */}
            {selectedSupplier && (
                <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-300">
                    
                    {/* BOTÃO VOLTAR */}
                    <button 
                        onClick={() => setSelectedSupplier(null)} 
                        className="flex items-center gap-2 text-gray-500 hover:text-gray-800 font-medium transition-colors"
                    >
                        <ArrowLeft size={18} /> Voltar para resultados
                    </button>

                    {/* FICHA DO FORNECEDOR */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col md:flex-row gap-6 items-start md:items-center">
                        <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shrink-0 shadow-inner">
                            <Building2 size={32} />
                        </div>
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-6 w-full">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 mb-1">{selectedSupplier.name}</h2>
                                <p className="text-sm font-medium text-gray-500 flex items-center gap-1">
                                    <FileText size={14} /> {selectedSupplier.document_type || 'DOC'}: {selectedSupplier.document_id || 'Não informado'}
                                </p>
                            </div>
                            <div className="space-y-1 pt-1 sm:border-l sm:border-gray-100 sm:pl-6">
                                <p className="text-sm text-gray-600 flex items-center gap-2">
                                    <Mail size={14} className="text-gray-400" /> {selectedSupplier.email || 'Sem e-mail'}
                                </p>
                                <p className="text-sm text-gray-600 flex items-center gap-2">
                                    <Phone size={14} className="text-gray-400" /> {selectedSupplier.phone || 'Sem telefone'}
                                </p>
                            </div>
                            <div className="space-y-1 pt-1 sm:border-l sm:border-gray-100 sm:pl-6">
                                <p className="text-sm text-gray-600 flex items-center gap-2">
                                    <User size={14} className="text-gray-400" /> Contato: {selectedSupplier.billing_contact_name || '-'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* CONDIÇÕES COMERCIAIS GERAIS (Atreladas ao Fornecedor) */}
                    <div className="bg-blue-50/30 rounded-xl shadow-sm border border-blue-100 p-6">
                        <h3 className="font-bold text-blue-800 flex items-center gap-2 mb-4">
                            <Handshake size={18} /> Condições Comerciais Gerais
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-1">
                                    <Banknote size={14} className="text-gray-500"/> Condição de Pagamento Negociada
                                </label>
                                <select
                                    value={supplierPaymentCondition}
                                    onChange={(e) => setSupplierPaymentCondition(e.target.value)}
                                    disabled={!canSave}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-700 bg-white transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <option value="">-- Selecione uma condição --</option>
                                    {paymentConditions.map(cond => (
                                        <option key={cond.id} value={cond.id}>{cond.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-1">
                                    <Clock size={14} className="text-gray-500"/> Prazo de Entrega Acordado
                                </label>
                                <input 
                                    type="text"
                                    value={supplierDeliveryTime}
                                    onChange={(e) => setSupplierDeliveryTime(e.target.value)}
                                    disabled={!canSave}
                                    placeholder="Ex: 5 dias úteis, Imediato..."
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-700 bg-white transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-1">
                                <FileEdit size={14} className="text-gray-500"/> Observações da Negociação
                            </label>
                            <textarea 
                                value={supplierNotes}
                                onChange={(e) => setSupplierNotes(e.target.value)}
                                disabled={!canSave}
                                placeholder="Informações adicionais sobre o acordo com este fornecedor..."
                                rows={2}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-700 bg-white transition-all shadow-sm resize-none disabled:opacity-60 disabled:cursor-not-allowed"
                            />
                        </div>
                    </div>

                    {/* TABELA DE PRODUTOS VINCULADOS & VALORES */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center gap-2">
                            <Package size={18} className="text-blue-600" />
                            <h3 className="font-bold text-gray-800">Produtos Vinculados & Valores</h3>
                        </div>

                        {isLoadingData ? (
                            <div className="p-12 flex justify-center"><Loader2 size={32} className="animate-spin text-blue-600" /></div>
                        ) : negotiationItems.length === 0 ? (
                            <div className="p-12 text-center text-gray-500 flex flex-col items-center">
                                <Package size={48} className="text-gray-300 mb-3" />
                                <p className="font-medium text-gray-700 mb-1">Nenhum produto vinculado.</p>
                                <p className="text-sm">Vá até o Cadastro de Produtos para vincular as embalagens a este fornecedor.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm min-w-[500px]">
                                    <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-[10px] border-b border-gray-200">
                                        <tr>
                                            <th className="px-6 py-4">PRODUTO / VARIAÇÃO</th>
                                            <th className="px-6 py-4 text-center w-48">EMBALAGEM (FATOR)</th>
                                            <th className="px-6 py-4 w-48">VALOR NEGOCIADO</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {negotiationItems.map((item, index) => (
                                            <tr key={item.id || index} className="hover:bg-blue-50/30 transition-colors">
                                                {/* Nome do Produto */}
                                                <td className="px-6 py-3 font-medium text-gray-800">
                                                    {item.product_name}
                                                </td>
                                                
                                                {/* Embalagem e Fator */}
                                                <td className="px-6 py-3 text-center">
                                                    <span className="font-bold text-gray-700 bg-gray-100 px-2 py-1 rounded">
                                                        {item.packaging_type}
                                                    </span>
                                                    <span className="text-xs text-gray-500 ml-2 block sm:inline mt-1 sm:mt-0">
                                                        (Fator: {item.conversion_factor})
                                                    </span>
                                                </td>

                                                {/* Valor Negociado */}
                                                <td className="px-6 py-3 relative">
                                                    <span className="absolute left-9 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium pointer-events-none z-10">R$</span>
                                                    <input 
                                                        type="text"
                                                        value={new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.negotiated_price || 0)}
                                                        onChange={(e) => handlePriceChange(index, e.target.value)}
                                                        disabled={!canSave}
                                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-right font-bold text-blue-700 transition-all bg-white shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        
                        {/* RODAPÉ E BOTÃO SALVAR SÓ APARECEM SE TIVER PERMISSÃO DE CRIAR/EDITAR */}
                        {negotiationItems.length > 0 && canSave && (
                            <div className="bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
                                <button 
                                    onClick={handleSaveNegotiation}
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm font-semibold transition-colors disabled:bg-green-400"
                                >
                                    {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                    {isSaving ? 'Salvando...' : 'Salvar Negociação'}
                                </button>
                            </div>
                        )}
                    </div>

                </div>
            )}
        </div>
    );
};

export default SupplierNegotiationPage;